import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSite } from '@/lib/SiteContext';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, CreditCard, CheckCircle2, Lock, Loader2, Info } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

export default function CheckoutPage() {
  const { cart, cartTotal, user } = useSite();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [iyzicoForm, setIyzicoForm] = useState(null);

  const [formData, setFormData] = useState({
    firstName: user?.displayName?.split(' ')[0] || '',
    lastName: user?.displayName?.split(' ')[1] || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: 'İstanbul',
    district: '',
  });

  // iyzico Ödeme Başlatma Fonksiyonu
  const handlePayment = async (e) => {
    e.preventDefault();
    if (!formData.phone || !formData.address) {
      alert("Lütfen tüm alanları doldurun.");
      return;
    }

    setLoading(true);
    const functions = getFunctions();
    const createPayment = httpsCallable(functions, 'createPayment');

    try {
      const result = await createPayment({
        customer: formData,
        cart: cart,
        total: cartTotal
      });

      if (result.data.checkoutContent) {
        setIyzicoForm(result.data.checkoutContent);
        // Form geldikten sonra script'i manuel tetikliyoruz
        setTimeout(() => {
          const scripts = document.getElementById('iyzico-container').getElementsByTagName('script');
          for (let i = 0; i < scripts.length; i++) {
            window.eval(scripts[i].innerHTML);
          }
        }, 500);
      }
    } catch (error) {
      console.error("Ödeme hatası:", error);
      alert("Ödeme başlatılamadı: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0 && !iyzicoForm) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-brand-cream p-4">
        <p className="text-brand-dark/40 mb-6 font-medium">Sepetiniz boş olduğu için ödeme yapamazsınız.</p>
        <Link to="/urunler" className="px-10 py-4 bg-brand-dark text-white rounded-full font-bold shadow-xl shadow-brand-dark/10 transition-transform active:scale-95">Alışverişe Başla</Link>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-brand-cream pt-32 pb-20 overflow-x-hidden">
      <div className="container mx-auto px-4 max-w-6xl">
        <AnimatePresence mode="wait">
          {!iyzicoForm ? (
            <motion.div 
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-12"
            >
              {/* SOL: Adres ve Bilgiler */}
              <div className="lg:col-span-7 space-y-8">
                <div className="flex items-center gap-4 mb-4">
                  <button onClick={() => navigate(-1)} className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"><ArrowLeft size={20}/></button>
                  <h1 className="text-4xl font-black tracking-tighter text-brand-dark">Checkout</h1>
                </div>

                <form className="space-y-6">
                  <div className="bg-white p-8 md:p-10 rounded-[3rem] shadow-sm border border-slate-100">
                    <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-brand-dark/30 mb-8 flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-brand-blue/10 flex items-center justify-center text-brand-blue text-[10px]">01</div>
                      Teslimat Bilgileri
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-2">Ad</label>
                        <input type="text" value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} className="w-full h-14 px-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand-dark/5 font-medium" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-2">Soyad</label>
                        <input type="text" value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} className="w-full h-14 px-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand-dark/5 font-medium" />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-2">Telefon</label>
                        <input type="tel" placeholder="05XX XXX XX XX" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full h-14 px-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand-dark/5 font-medium" />
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-2">Tam Adres</label>
                        <textarea rows="4" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full p-6 rounded-3xl bg-slate-50 border-none focus:ring-2 focus:ring-brand-dark/5 resize-none font-medium" placeholder="Mahalle, sokak, no..."></textarea>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-8 md:p-10 rounded-[3rem] shadow-sm border border-slate-100">
                    <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-brand-dark/30 mb-6 flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-50 flex items-center justify-center text-green-600 text-[10px]">02</div>
                      Ödeme Yöntemi
                    </h2>
                    <div className="flex items-center gap-4 p-5 rounded-3xl bg-slate-50 border border-slate-100">
                      <CreditCard className="text-brand-dark/40" />
                      <div>
                        <p className="text-sm font-bold">iyzico Güvenli Ödeme</p>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Kredi / Banka Kartı</p>
                      </div>
                      <CheckCircle2 className="ml-auto text-brand-dark" size={20} />
                    </div>
                  </div>
                </form>
              </div>

              {/* SAĞ: Özet ve Buton */}
              <div className="lg:col-span-5">
                <div className="sticky top-32 space-y-6">
                  <div className="bg-brand-dark text-white p-10 rounded-[3.5rem] shadow-2xl shadow-brand-dark/20 relative overflow-hidden">
                    <div className="relative z-10">
                      <h3 className="text-2xl font-black mb-10 tracking-tighter">Sipariş Özeti</h3>
                      
                      <div className="space-y-6 mb-10 max-h-[250px] overflow-y-auto pr-2 scrollbar-hide">
                        {cart.map((item) => (
                          <div key={item.id} className="flex justify-between items-center gap-4">
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 rounded-2xl bg-white/10 overflow-hidden shrink-0 border border-white/5">
                                <img src={item.images?.[0]} className="w-full h-full object-cover" alt="" />
                              </div>
                              <div>
                                <p className="text-sm font-bold leading-tight">{item.name}</p>
                                <p className="text-[10px] font-bold text-white/40 uppercase tracking-widest">{item.quantity} Adet</p>
                              </div>
                            </div>
                            <span className="text-sm font-black">{(item.price * item.quantity).toLocaleString('tr-TR')} TL</span>
                          </div>
                        ))}
                      </div>

                      <div className="border-t border-white/10 pt-8 space-y-4">
                        <div className="flex justify-between text-[10px] text-white/30 font-black uppercase tracking-[0.2em]">
                          <span>Ara Toplam</span>
                          <span>{cartTotal.toLocaleString('tr-TR')} TL</span>
                        </div>
                        <div className="flex justify-between text-[10px] text-white/30 font-black uppercase tracking-[0.2em]">
                          <span>Kargo</span>
                          <span className="text-green-400">Ücretsiz</span>
                        </div>
                        <div className="flex justify-between text-3xl font-black tracking-tighter pt-4 border-t border-white/5">
                          <span>Toplam</span>
                          <span>{cartTotal.toLocaleString('tr-TR')} TL</span>
                        </div>
                      </div>

                      <button 
                        onClick={handlePayment}
                        disabled={loading}
                        className="w-full py-6 bg-white text-brand-dark rounded-full font-black text-xl mt-10 hover:bg-brand-pink transition-all active:scale-95 flex items-center justify-center gap-3 shadow-xl shadow-black/20"
                      >
                        {loading ? <Loader2 className="animate-spin" /> : <>Ödemeye Geç <Lock size={20}/></>}
                      </button>

                      {/* YENİ EKLENEN: MAY PLASTİK GÜVENCE BİLDİRİMİ */}
                      <div className="mt-8 p-5 rounded-[1.5rem] bg-white/5 border border-white/10 flex items-start gap-3 backdrop-blur-sm">
                        <ShieldCheck className="text-brand-blue shrink-0 mt-0.5" size={18} />
                        <div>
                          <p className="text-[10px] font-bold text-white/60 uppercase tracking-widest mb-1">Yetkili Satış Kanalı</p>
                          <p className="text-xs text-white/40 leading-relaxed font-medium">
                            Ödeme işlemleriniz ve faturanız, resmi üretici <strong className="text-white">May Plastik</strong> altyapısı ve güvencesiyle gerçekleştirilmektedir. Ekstrenizde "May Plastik" yer alacaktır.
                          </p>
                        </div>
                      </div>

                    </div>
                    {/* Arka plan süsü */}
                    <div className="absolute -right-20 -top-20 w-64 h-64 bg-brand-blue/10 rounded-full blur-3xl" />
                  </div>
                  
                  <div className="flex items-center justify-center gap-3 text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                    <Lock size={14} /> 256-bit SSL Güvenli İşlem
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            /* iyzico Formunun Enjekte Edildiği Alan */
            <motion.div 
              key="iyzico"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              className="max-w-2xl mx-auto bg-white p-10 rounded-[3rem] shadow-2xl relative overflow-hidden"
            >
              <div className="text-center mb-8 relative z-10">
                <div className="w-20 h-20 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-6 shadow-sm border border-slate-100">
                  <CreditCard className="text-brand-dark" size={32} />
                </div>
                <h2 className="text-3xl font-black tracking-tighter mb-2">Güvenli Ödeme Hattı</h2>
                <p className="text-sm text-slate-500 font-medium">Kart bilgilerinizi iyzico altyapısıyla güvenle girin.</p>
              </div>

              {/* YENİ EKLENEN: İYZİCO FORM ÜSTÜ GÜVENCE BİLDİRİMİ */}
              <div className="mb-10 p-5 rounded-3xl bg-brand-blue/5 border border-brand-blue/10 flex items-center gap-4 text-brand-dark relative z-10">
                <div className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center shrink-0">
                  <Info className="text-brand-blue" size={20} />
                </div>
                <p className="text-xs font-medium text-slate-600 text-left leading-relaxed">
                  Bu ödeme işlemi <strong className="text-brand-dark">May Plastik</strong> yetkili satış altyapısı üzerinden gerçekleşmektedir.
                </p>
              </div>
              
              <div id="iyzico-container" className="min-h-[500px] relative z-10">
                <div dangerouslySetInnerHTML={{ __html: iyzicoForm }} />
              </div>

              <button 
                onClick={() => setIyzicoForm(null)}
                className="mt-8 text-slate-400 text-xs font-bold uppercase tracking-widest hover:text-brand-dark transition-colors mx-auto block relative z-10"
              >
                Geri Dön ve Bilgileri Güncelle
              </button>

              {/* Form arka plan süsü */}
              <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-slate-50 rounded-full blur-3xl pointer-events-none" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}